import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

export default function Settings() {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(null)
  const [toast, setToast] = useState(null)
  const [activeTab, setActiveTab] = useState(null)
  const [editingCats, setEditingCats] = useState(new Set())
  const [showAddForm, setShowAddForm] = useState(false)
  const [newProduct, setNewProduct] = useState({ name: '', price: '', category: '' })
  const [showArchived, setShowArchived] = useState(false)
  const [confirm, setConfirm] = useState(null) // { message, onConfirm }

  useEffect(() => { fetchProducts() }, [])

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000) }

  const fetchProducts = async () => {
    setLoading(true)
    const { data } = await supabase.from('products').select('*').order('category').order('sort_order')
    setProducts(data || [])
    setLoading(false)
  }

  // Only non-archived products form tabs
  const activeProducts = products.filter(p => p.active !== false)
  const archivedProducts = products.filter(p => p.active === false)
  const categories = [...new Set(activeProducts.map(p => p.category))]

  useEffect(() => {
    if (categories.length > 0 && (activeTab === null || !categories.includes(activeTab)))
      setActiveTab(categories[0])
  }, [products])

  const updateProduct = (id, field, value) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p))
  }

  const saveCategory = async (cat) => {
    setSaving(cat)
    for (const p of products.filter(q => q.category === cat && q.active !== false)) {
      await supabase.from('products').update({ name: p.name, price: p.price }).eq('id', p.id)
    }
    setEditingCats(prev => { const s = new Set(prev); s.delete(cat); return s })
    showToast('Saved!')
    setSaving(null)
  }

  const cancelEdit = async (cat) => {
    setEditingCats(prev => { const s = new Set(prev); s.delete(cat); return s })
    await fetchProducts()
  }

  const archiveProduct = async (product) => {
    const { error } = await supabase.from('products').update({ active: false }).eq('id', product.id)
    if (error) { showToast('Error: ' + error.message); return }
    await fetchProducts()
    showToast('Product archived')
  }

  const restoreProduct = async (product) => {
    const { error } = await supabase.from('products').update({ active: true }).eq('id', product.id)
    if (error) { showToast('Error: ' + error.message); return }
    await fetchProducts()
    showToast('Product restored')
  }

  const checkTransactions = async (productIds) => {
    for (const id of productIds) {
      const { count } = await supabase.from('sales').select('id', { count: 'exact', head: true }).contains('items', [{ id }])
      if (count > 0) return true
    }
    return false
  }

  const askConfirm = (message, onConfirm) => setConfirm({ message, onConfirm })

  const deleteProduct = async (product) => {
    const hasTransactions = await checkTransactions([product.id])
    const msg = hasTransactions
      ? `"${product.name}" has existing transactions. History will be kept but this product will be permanently removed. Delete anyway?`
      : `Delete "${product.name}"?`
    askConfirm(msg, async () => {
      const { error } = await supabase.from('products').delete().eq('id', product.id)
      if (error) { showToast('Error: ' + error.message); return }
      await fetchProducts()
      showToast('Product deleted')
    })
  }

  const deleteCategory = async (cat) => {
    const catProducts = products.filter(p => p.category === cat)
    const hasTransactions = await checkTransactions(catProducts.map(p => p.id))
    const msg = hasTransactions
      ? `"${cat}" has existing transactions. History will be kept but all products in this category will be permanently removed. Delete anyway?`
      : `Delete entire "${cat}" category and all its products?`
    askConfirm(msg, async () => {
      const { error } = await supabase.from('products').delete().in('id', catProducts.map(p => p.id))
      if (error) { showToast('Error: ' + error.message); return }
      await fetchProducts()
      setActiveTab(prev => prev === cat ? null : prev)
      setEditingCats(prev => { const s = new Set(prev); s.delete(cat); return s })
      showToast('Category deleted')
    })
  }

  const addProduct = async () => {
    if (!newProduct.name.trim() || !newProduct.price || !newProduct.category.trim()) return
    const { error } = await supabase.from('products').insert({
      name: newProduct.name.trim(),
      price: parseFloat(newProduct.price),
      category: newProduct.category.trim(),
      active: true,
      sort_order: 999
    })
    if (error) { showToast('Error: ' + (error.message || 'Could not add product')); return }
    const cat = newProduct.category.trim()
    setNewProduct({ name: '', price: '', category: '' })
    setShowAddForm(false)
    await fetchProducts()
    setActiveTab(cat)
    showToast('Product added!')
  }

  if (loading) return <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>Loading...</div>

  const tabProducts = activeProducts.filter(p => p.category === activeTab)
  const isEditing = editingCats.has(activeTab)

  return (
    <div style={{ padding: 16, paddingBottom: 100 }}>
      {toast && (
        <div style={{ position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', background: '#1a6b3c', color: '#fff', padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 500, zIndex: 999 }}>
          {toast}
        </div>
      )}

      {confirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 320, width: '100%' }}>
            <div style={{ fontSize: 14, color: '#1a3a2a', marginBottom: 20, lineHeight: 1.5 }}>{confirm.message}</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setConfirm(null)}
                style={{ flex: 1, padding: '10px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={() => { const fn = confirm.onConfirm; setConfirm(null); fn() }}
                style={{ flex: 1, padding: '10px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 20, color: '#1a3a2a' }}>Settings</div>
        <button onClick={() => setShowAddForm(v => !v)}
          style={{ background: '#1a6b3c', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          + Add Product
        </button>
      </div>

      {/* Add Product Form */}
      {showAddForm && (
        <div style={{ background: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#1a3a2a', marginBottom: 10 }}>New Product</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input placeholder="Product name" value={newProduct.name}
              onChange={e => setNewProduct(p => ({ ...p, name: e.target.value }))}
              style={{ padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: 7, fontSize: 13, outline: 'none' }} />
            <input placeholder="Price" type="number" step="0.5" value={newProduct.price}
              onChange={e => setNewProduct(p => ({ ...p, price: e.target.value }))}
              style={{ padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: 7, fontSize: 13, outline: 'none' }} />
            <input list="cat-options" placeholder="Category (select or type new)" value={newProduct.category}
              onChange={e => setNewProduct(p => ({ ...p, category: e.target.value }))}
              style={{ padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: 7, fontSize: 13, outline: 'none' }} />
            <datalist id="cat-options">
              {categories.map(c => <option key={c} value={c} />)}
            </datalist>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={addProduct}
                style={{ flex: 1, background: '#1a6b3c', color: '#fff', border: 'none', borderRadius: 7, padding: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Add
              </button>
              <button onClick={() => { setShowAddForm(false); setNewProduct({ name: '', price: '', category: '' }) }}
                style={{ flex: 1, background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: 7, padding: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Tabs */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
        {categories.map(cat => (
          <button key={cat} onClick={() => setActiveTab(cat)}
            style={{ padding: '7px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              background: activeTab === cat ? '#1a6b3c' : '#f3f4f6',
              color: activeTab === cat ? '#fff' : '#6b7280' }}>
            {cat}
          </button>
        ))}
      </div>

      {/* Active Category Products */}
      {activeTab && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, paddingBottom: 6, borderBottom: '2px solid #e5e7eb' }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1a3a2a' }}>{activeTab}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {isEditing ? (
                <>
                  <button onClick={() => saveCategory(activeTab)} disabled={saving === activeTab}
                    style={{ background: '#1a6b3c', color: '#fff', border: 'none', borderRadius: 7, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    {saving === activeTab ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={() => cancelEdit(activeTab)}
                    style={{ background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: 7, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button onClick={() => deleteCategory(activeTab)}
                    style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 7, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    Delete Category
                  </button>
                </>
              ) : (
                <button onClick={() => setEditingCats(prev => new Set([...prev, activeTab]))}
                  style={{ background: '#f3f4f6', color: '#1a3a2a', border: 'none', borderRadius: 7, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Edit
                </button>
              )}
            </div>
          </div>

          {tabProducts.map(p => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              {isEditing ? (
                <>
                  <input value={p.name} onChange={e => updateProduct(p.id, 'name', e.target.value)}
                    style={{ flex: 1, padding: '7px 10px', border: '1.5px solid #e5e7eb', borderRadius: 7, fontSize: 13, outline: 'none' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 13, color: '#6b7280' }}>$</span>
                    <input value={p.price} onChange={e => updateProduct(p.id, 'price', e.target.value)} type="number" step="0.5"
                      style={{ width: 70, padding: '7px 8px', border: '1.5px solid #e5e7eb', borderRadius: 7, fontSize: 13, outline: 'none' }} />
                  </div>
                  <button onClick={() => archiveProduct(p)}
                    style={{ padding: '6px 10px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, background: '#fef3c7', color: '#92400e' }}>
                    Archive
                  </button>
                  <button onClick={() => deleteProduct(p)}
                    style={{ padding: '6px 9px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, background: '#fee2e2', color: '#dc2626' }}
                    title="Delete product">
                    &#x1F5D1;
                  </button>
                </>
              ) : (
                <>
                  <div style={{ flex: 1, padding: '7px 2px', fontSize: 13, color: '#1a3a2a' }}>{p.name}</div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>${Number(p.price).toFixed(2)}</div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Archived Products */}
      {archivedProducts.length > 0 && (
        <div style={{ marginTop: 28, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
          <button onClick={() => setShowArchived(v => !v)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: showArchived ? 12 : 0 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#6b7280' }}>Archived Products ({archivedProducts.length})</span>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>{showArchived ? '▲' : '▼'}</span>
          </button>
          {showArchived && archivedProducts.map(p => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, opacity: 0.7 }}>
              <div style={{ flex: 1, fontSize: 13, color: '#6b7280' }}>{p.name}</div>
              <div style={{ fontSize: 12, color: '#9ca3af' }}>{p.category} · ${Number(p.price).toFixed(2)}</div>
              <button onClick={() => restoreProduct(p)}
                style={{ padding: '5px 10px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, background: '#e8f5ee', color: '#1a6b3c' }}>
                Restore
              </button>
              <button onClick={() => deleteProduct(p)}
                style={{ padding: '5px 8px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 12, background: '#fee2e2', color: '#dc2626' }}
                title="Delete permanently">
                &#x1F5D1;
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
